import React, { useEffect, useRef, useState } from 'react';
import { ContactFormStyles } from './ContactFormStyles';
import {
  Form, Formik, FormikHelpers, FormikProps,
} from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import InputField from '../form/inputField/InputField';
import SquareCheckbox from '../form/squareCheckbox/SquareCheckbox';
import { useAppSelector } from '../../state/hooks';
import { translations } from '../../i18n/translations';
import { InView } from 'react-intersection-observer';
import { send } from 'emailjs-com';
import ShowTextByLetter from '../../utils/ShowTextByLetter/ShowTextByLetter';
import { Api } from '../../api';

export const phoneRegExp = /^(\+\d{1,3}[- ]?)?\d{10}$/;

interface FormValues {
  name: string;
  phone: string;
  email: string;
  surname: string;
  privacyPolicy: boolean;
}

const initialValues: FormValues = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  privacyPolicy: false,
};

interface Props {
  emailDescription: string;
  withoutTitle?: boolean;
  inModal?: boolean;
}

function ContactForm({ withoutTitle, emailDescription, inModal }: Props) {
  const navigate = useNavigate();
  const { locale } = useParams();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [submitError, setSubmitError] = useState(false);

  const formRef = useRef<any>();
  const { interfaceLanguage } = useAppSelector((state) => state.languages);

  useEffect(() => {
    if (formRef.current?.handleReset) formRef.current.handleReset();
  }, [location.pathname]);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(translations[interfaceLanguage].validation_email)
      .required(translations[interfaceLanguage].validation_required),
    name: Yup.string()
      .required(translations[interfaceLanguage].validation_required),
    phone: Yup.string()
      .matches(phoneRegExp, translations[interfaceLanguage].validation_phone)
      .required(translations[interfaceLanguage].validation_required),
    surname: Yup.string()
      .required(translations[interfaceLanguage].validation_required),
    privacyPolicy: Yup.boolean()
      .required(translations[interfaceLanguage].validation_required),
  });

  function getErrorMessage(key: string) {
    return typeof errorMessage === 'object'
      ? errorMessage[key]
      && `* ${errorMessage[key].charAt(0).toUpperCase()}${errorMessage[key].substring(1)}`
      : undefined;
  }

  function showSubmitError() {
    setSubmitError(true);
    setTimeout(() => setSubmitError(false), 4000);
  }

  async function onSubmit(values: FormValues) {
    const data = {
      name: values.name,
      surname: values.surname,
      email: values.email,
      phone: values.phone,
      description: emailDescription,
      path: `https://realist.ua${location.pathname}`,
      urlParameters: location.search,
      lang: interfaceLanguage,
    };

    setIsLoading(true);
    setSubmitError(false);

    try {
      const [apiResult] = await Promise.allSettled([
        Api.sendData(data),
        send('service_zpbbvrv', 'template_5tbezfq', data, 'yTJ5l_KR9ivLMR648'),
      ]);

      if (apiResult.status === 'fulfilled') {
        const res = apiResult.value;
        if (res?.statusCode >= 200 && res?.statusCode < 300) {
          navigate(`/${interfaceLanguage.toLowerCase()}/thank-you`);
          return;
        }
        showSubmitError();
        return;
      }

      showSubmitError();
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyUp(key: string, error: string | undefined) {
    setErrorMessage(typeof errorMessage === 'object' ? { ...errorMessage, [key]: error } : errorMessage);
  }

  const renderForm = ({
    values,
    touched,
    setFieldValue,
    isSubmitting,
  }: FormikProps<FormValues>) => (
    <Form id="form">
      <InView
        className="formWrapper"
        threshold={0.5}
        as="div"
        onChange={(inView, entry) => (inView ? entry.target.classList.add('active') : '')}
      >
        <div className="form">
          <InputField
            name="name"
            value={values.name}
            placeholder={translations[interfaceLanguage].form_name_placeholder}
            label={translations[interfaceLanguage].form_name_label}
            onChange={setFieldValue}
            error={typeof errorMessage === 'object' && getErrorMessage('name') && touched.name ? getErrorMessage('name') : ''}
          />

          <InputField
            name="surname"
            value={values.surname}
            placeholder={translations[interfaceLanguage].form_surname_placeholder}
            label={translations[interfaceLanguage].form_surname_label}
            onChange={setFieldValue}
            error={typeof errorMessage === 'object' && getErrorMessage('surname') && touched.surname ? getErrorMessage('surname') : ''}
          />

          <InputField
            name="email"
            value={values.email}
            placeholder="name@example.com"
            label="Email"
            onChange={setFieldValue}
            error={typeof errorMessage === 'object' && getErrorMessage('email') && touched.email ? getErrorMessage('email') : ''}
          />

          <InputField
            name="phone"
            value={values.phone}
            placeholder="+380"
            label={translations[interfaceLanguage].form_phone_label}
            onChange={setFieldValue}
            error={typeof errorMessage === 'object' && getErrorMessage('phone') && touched.phone ? getErrorMessage('phone') : ''}
          />

          <SquareCheckbox
            value={values.privacyPolicy}
            name="privacyPolicy"
            onChange={() => setFieldValue('privacyPolicy', !values.privacyPolicy)}
            error={typeof errorMessage === 'object' && getErrorMessage('privacyPolicy') && touched.privacyPolicy ? getErrorMessage('privacyPolicy') : ''}
            showError
          >
            <span className="textBase16">{translations[interfaceLanguage].form_privacy_policy}</span>
          </SquareCheckbox>

          <button type="submit" className="button" disabled={isSubmitting || isLoading}>
            {translations[interfaceLanguage].submit_button}
          </button>

          {submitError && (
            <p className="error">
              *
              {translations[interfaceLanguage].submit_error}
            </p>
          )}
        </div>
      </InView>
    </Form>
  );

  return (
    <ContactFormStyles className={inModal ? 'inModal contactFormWrapper' : 'contactFormWrapper'}>
      <div className="container">
        {!withoutTitle && (
          <h2>
            {ShowTextByLetter(translations[interfaceLanguage].form_title)}
          </h2>
        )}

        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {renderForm}
        </Formik>
      </div>
    </ContactFormStyles>
  );
}

export default ContactForm;
